import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertTriangle,
  FiRefreshCw,
  FiExternalLink,
  FiXCircle,
} from "react-icons/fi";

import {
  getCatalog,
  getMyBilling,
  cancelSubscription,
  reactivateSubscription,
  formatBRL,
  formatDate,
  intervalLabel,
  getErrorMessage,
  getErrorCode,
  SUBSCRIPTION_STATUS_LABELS,
} from "../services/billingApi";
import CheckoutModal from "../components/checkout/CheckoutModal";
import CompanyRequiredState from "../components/CompanyRequiredState";
import "./AssinaturaPage.css";

const CheckIcon = () => (
  <svg viewBox="0 0 12 12">
    <polyline points="2 6 5 9 10 3" />
  </svg>
);

/* ── Rótulos das funcionalidades do catálogo ───────────────────────────────── */

const POSICIONAMENTO_LABELS = {
  PADRAO: "Listagem padrão",
  PRIORITARIO: "Prioritária (acima dos gratuitos)",
  TOPO: "Topo das categorias & banner em rotação",
};

const MATERIAL_PDF_LABELS = {
  NENHUM: "Não disponível",
  TABELA_FICHA: "Tabela de Preços / Ficha Técnica",
  TABELA_FICHA_LAUDOS: "Tabela / Ficha Técnica / Laudos",
};

const SELO_LABELS = {
  PERFIL_BASICO: "Perfil básico",
  CNPJ_VERIFICADO: 'Selo "CNPJ Verificado"',
  FORNECEDOR_DESTAQUE_PREMIUM: 'Selo "Fornecedor Destaque Premium"',
};

const METRICAS_LABELS = {
  BASICO: "Visualizações básicas",
  CLIQUES_COTACOES: "Métricas de cliques e cotações",
  RELATORIO_REGIONAL: "Relatório completo de pesquisas regionais",
};

const SUPORTE_LABELS = {
  COMUNIDADE: "Central de Ajuda / E-mail",
  EMAIL: "Suporte por e-mail",
  PRIORITARIO: "Suporte prioritário",
  PRIORITARIO_24H: "Prioritário em até 24h",
  WHATSAPP_DEDICADO: "Atendimento dedicado via WhatsApp",
};

const photosLabel = (features) => {
  const total = Number(features.fotosPorAnuncio || 1);
  return total > 1 ? `Até ${total} fotos por item` : "1 foto por item";
};

const whatsappLabel = (features) => {
  if (!features.whatsappDireto) return "Não (via e-mail)";
  return features.atendimentoHumano
    ? "Sim (link direto + atendimento humano)"
    : "Sim (link direto no anúncio)";
};

const productsLabel = (plan) =>
  plan.isUnlimitedProducts
    ? "Produtos ilimitados"
    : `Até ${plan.productsLimit} produto${plan.productsLimit > 1 ? "s" : ""}`;

/**
 * Bullets do card. A copy oficial vem do catálogo (`features.highlights`); se o
 * seed ainda não tiver rodado, montamos a lista pelas flags do plano.
 */
const buildFeatureList = (plan) => {
  const features = plan.features || {};

  if (Array.isArray(features.highlights) && features.highlights.length) {
    return features.highlights;
  }

  const list = [
    productsLabel(plan),
    photosLabel(features),
    features.perfilEmpresa === "COMPLETO"
      ? "Perfil de empresa completo"
      : "Perfil de empresa básico",
  ];

  if (features.whatsappDireto) list.push("Botão de WhatsApp direto no anúncio");
  if (features.materialPdf && features.materialPdf !== "NENHUM") {
    list.push(MATERIAL_PDF_LABELS[features.materialPdf]);
  }
  if (features.destaqueIncluso > 0) {
    list.push(
      `${features.destaqueIncluso} destaque de produto incluso${
        features.destaqueIncluso > 1 ? "s" : ""
      }`
    );
  }
  if (features.bancoDemandas) list.push("Acesso ao Banco de Demandas");
  if (features.posicaoPreferencial) {
    list.push(POSICIONAMENTO_LABELS[features.posicionamentoBusca] || "Busca prioritária");
  }
  list.push(METRICAS_LABELS[features.metricas] || "Visualizações básicas");
  list.push(SUPORTE_LABELS[features.suporte] || "Central de Ajuda / E-mail");

  return list;
};

/**
 * Agrupa mensal + anual do mesmo plano comercial (Gratuito, Pro, Premium) para
 * a página exibir um card por família e alternar o preço pelo seletor de ciclo.
 */
const groupPlansByFamily = (plans) => {
  const groups = new Map();

  plans.forEach((plan) => {
    const family = plan.features?.family || plan.code;
    const group = groups.get(family) || {
      family,
      monthly: null,
      annual: null,
      sortOrder: plan.sortOrder,
    };

    if (plan.interval === "YEAR") group.annual = plan;
    else group.monthly = plan;

    group.sortOrder = Math.min(group.sortOrder, plan.sortOrder);
    groups.set(family, group);
  });

  return [...groups.values()].sort((a, b) => a.sortOrder - b.sortOrder);
};

/** Quanto o ciclo anual economiza frente a 12 cobranças mensais. */
const annualSavingsCents = (group) => {
  if (!group.monthly || !group.annual || group.monthly.priceCents <= 0) return 0;
  return group.monthly.priceCents * 12 - group.annual.priceCents;
};

/** Linhas da tabela comparativa — mesma ordem da tabela comercial oficial. */
const COMPARISON_ROWS = [
  { label: "Limite de produtos", value: (group) => productsLabel(group.plan) },
  {
    label: "Valor mensal",
    value: (group) =>
      group.monthly ? formatBRL(group.monthly.priceCents) + "/mês" : "—",
  },
  {
    label: "Valor anual",
    value: (group) =>
      group.annual ? formatBRL(group.annual.priceCents) + "/ano" : "—",
  },
  {
    label: "Economia no plano anual",
    value: (group) => {
      const savings = annualSavingsCents(group);
      return savings > 0 ? `Economia de ${formatBRL(savings)}/ano` : "—";
    },
  },
  {
    label: "Posicionamento nas buscas",
    value: (group) =>
      POSICIONAMENTO_LABELS[group.features.posicionamentoBusca] || "Listagem padrão",
  },
  { label: "Contato direto via WhatsApp", value: (group) => whatsappLabel(group.features) },
  { label: "Fotos por anúncio", value: (group) => photosLabel(group.features) },
  {
    label: "Material complementar (PDF)",
    value: (group) => MATERIAL_PDF_LABELS[group.features.materialPdf] || "Não disponível",
  },
  {
    label: "Selos de confiança",
    value: (group) => SELO_LABELS[group.features.seloConfianca] || "Perfil básico",
  },
  {
    label: "Acesso ao Banco de Demandas",
    value: (group) =>
      group.features.bancoDemandas ? "Acesso total a pedidos de compradores" : "Bloqueado",
  },
  {
    label: "Métricas e relatórios",
    value: (group) => METRICAS_LABELS[group.features.metricas] || "Visualizações básicas",
  },
  {
    label: "Suporte comercial",
    value: (group) => SUPORTE_LABELS[group.features.suporte] || "Central de Ajuda / E-mail",
  },
];

const AssinaturaPage = () => {
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkoutPlanItem, setCheckoutPlanItem] = useState(null);
  // Ciclo escolhido no seletor: "MONTH" (mensal) ou "YEAR" (anual).
  const [period, setPeriod] = useState("MONTH");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorCode(null);
    try {
      const [catalogData, billingData] = await Promise.all([getCatalog(), getMyBilling()]);
      setCatalog(catalogData);
      setBilling(billingData);
      // Abre a página já no ciclo que o fornecedor assina hoje.
      if (billingData?.entitlements?.planInterval === "YEAR") setPeriod("YEAR");
      setError(null);
    } catch (loadError) {
      setErrorCode(getErrorCode(loadError));
      setError(getErrorMessage(loadError, "Não foi possível carregar seus planos."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const subscription = billing?.subscription || null;
  const entitlements = billing?.entitlements || null;
  const currentPlanCode = entitlements?.planCode || "GRATUITO";

  const plans = useMemo(() => catalog?.plans || [], [catalog]);

  // Um card por plano comercial, com as variações mensal e anual dentro.
  const planFamilies = useMemo(() => groupPlansByFamily(plans), [plans]);

  const hasAnnualOption = planFamilies.some((group) => group.annual);

  // O grupo exibido já resolvido no ciclo escolhido — usado pelos cards e pela
  // tabela comparativa (as funcionalidades são iguais nos dois ciclos).
  const displayedGroups = useMemo(
    () =>
      planFamilies
        .map((group) => {
          const plan =
            period === "YEAR"
              ? group.annual || group.monthly
              : group.monthly || group.annual;

          return plan ? { ...group, plan, features: plan.features || {} } : null;
        })
        .filter(Boolean),
    [planFamilies, period]
  );

  const statusInfo =
    SUBSCRIPTION_STATUS_LABELS[entitlements?.subscriptionStatus] ||
    SUBSCRIPTION_STATUS_LABELS.SEM_ASSINATURA;

  const handleCancel = async (atPeriodEnd) => {
    const confirmation = atPeriodEnd
      ? "Cancelar a renovação? Você mantém o acesso até o fim do período já pago."
      : "Cancelar agora? Você perde o acesso imediatamente e volta ao plano gratuito.";

    if (!window.confirm(confirmation)) return;

    setBusy(true);
    try {
      const result = await cancelSubscription({ atPeriodEnd });
      setActionMessage(result?.message || "Assinatura cancelada.");
      await load();
    } catch (cancelError) {
      setActionMessage(getErrorMessage(cancelError, "Não foi possível cancelar."));
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    setBusy(true);
    try {
      await reactivateSubscription();
      setActionMessage("Assinatura reativada.");
      await load();
    } catch (reactivateError) {
      setActionMessage(getErrorMessage(reactivateError, "Não foi possível reativar."));
    } finally {
      setBusy(false);
    }
  };

  const handleCheckoutSuccess = useCallback(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="assinatura fade-in">
        <div className="assinatura__hero">
          <h1>Carregando seus planos...</h1>
        </div>
      </div>
    );
  }

  // Sem empresa cadastrada não é erro: é uma etapa que ainda falta.
  if (errorCode === "COMPANY_REQUIRED") {
    return (
      <div className="assinatura fade-in">
        <CompanyRequiredState />
      </div>
    );
  }

  if (error) {
    return (
      <div className="assinatura fade-in">
        <div className="assinatura__hero">
          <h1>Não conseguimos carregar</h1>
          <p>{error}</p>
        </div>
        <div className="assinatura__center">
          <button type="button" className="button button--primary" onClick={load}>
            <FiRefreshCw /> Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const limits = entitlements?.limits;
  const usagePercent =
    limits && limits.productsLimit > 0
      ? Math.min(100, Math.round((limits.productsUsed / limits.productsLimit) * 100))
      : 0;

  return (
    <div className="assinatura fade-in">
      {/* ── Situação atual ─────────────────────────────────────────────── */}
      <section className="sub-status">
        <div className="sub-status__main">
          <div className="sub-status__head">
            <span className="badge" data-tone={statusInfo.tone}>
              {statusInfo.label}
            </span>
            {subscription?.cancelAtPeriodEnd && (
              <span className="badge" data-tone="warning">
                Renovação cancelada
              </span>
            )}
          </div>

          <h2>{entitlements?.planName || "Plano Gratuito"}</h2>

          <p className="sub-status__meta">
            {subscription?.expiresAt ? (
              <>
                {subscription.autoRenew ? "Renova em" : "Válido até"}{" "}
                <strong>{formatDate(subscription.expiresAt)}</strong>
                {subscription.priceCents > 0 && (
                  <>
                    {" · "}
                    {formatBRL(subscription.priceCents)}
                    {intervalLabel(subscription.billingInterval)}
                  </>
                )}
              </>
            ) : (
              "Sem cobrança recorrente. Faça upgrade quando quiser."
            )}
          </p>

          {subscription?.status === "INADIMPLENTE" && (
            <p className="sub-status__alert">
              <FiAlertTriangle />
              Não conseguimos processar sua última cobrança
              {subscription.failedChargeCount > 1
                ? ` (${subscription.failedChargeCount} tentativas)`
                : ""}
              . Atualize o pagamento para não perder o acesso.
            </p>
          )}

          <div className="sub-status__actions">
            {subscription?.cancelAtPeriodEnd ? (
              <button
                type="button"
                className="button button--primary"
                onClick={handleReactivate}
                disabled={busy}
              >
                <FiRefreshCw /> Reativar renovação
              </button>
            ) : (
              subscription &&
              subscription.status !== "CANCELADA" &&
              subscription.priceCents > 0 && (
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => handleCancel(true)}
                  disabled={busy}
                >
                  <FiXCircle /> Cancelar renovação
                </button>
              )
            )}

            <button
              type="button"
              className="button button--ghost"
              onClick={() => navigate("/faturas")}
            >
              <FiExternalLink /> Ver faturas
            </button>
          </div>

          {actionMessage && <p className="sub-status__message">{actionMessage}</p>}
        </div>

        {/* Uso do limite de produtos — o gatilho de upgrade mais direto. */}
        {limits && (
          <div className="sub-usage">
            <p className="sub-usage__label">Produtos publicados</p>
            <p className="sub-usage__value">
              {limits.productsUsed}
              <span>
                {limits.isUnlimitedProducts ? " / ilimitado" : ` / ${limits.productsLimit}`}
              </span>
            </p>
            {!limits.isUnlimitedProducts && (
              <div className="sub-usage__bar">
                <span
                  style={{ width: `${usagePercent}%` }}
                  data-full={usagePercent >= 100 ? "true" : "false"}
                />
              </div>
            )}
            {!limits.isUnlimitedProducts && usagePercent >= 100 && (
              <p className="sub-usage__hint">
                Limite atingido — faça upgrade para publicar mais.
              </p>
            )}
            {limits.photosPerProduct > 0 && (
              <p className="sub-usage__note">
                {limits.photosPerProduct === 1
                  ? "1 foto por anúncio"
                  : `Até ${limits.photosPerProduct} fotos por anúncio`}
              </p>
            )}
          </div>
        )}
      </section>

      {/* ── Escada de planos ───────────────────────────────────────────── */}
      <div className="assinatura__hero">
        <h1>Escolha seu plano</h1>
        <p>
          Divulgue seus produtos para compradores B2B em todo o Brasil, sem pagar
          comissão sobre venda. Cancele quando quiser.
        </p>
      </div>

      {hasAnnualOption && (
        <div className="assinatura__toggle" role="group" aria-label="Ciclo de cobrança">
          <button
            type="button"
            className="assinatura__toggle-option"
            data-active={period === "MONTH"}
            onClick={() => setPeriod("MONTH")}
          >
            Mensal
          </button>
          <button
            type="button"
            className="assinatura__toggle-option"
            data-active={period === "YEAR"}
            onClick={() => setPeriod("YEAR")}
          >
            Anual <span className="assinatura__toggle-tag">~20% off</span>
          </button>
        </div>
      )}

      <div className="assinatura__grid">
        {displayedGroups.map((group) => {
          const plan = group.plan;
          const isCurrent = plan.code === currentPlanCode;
          const highlights = buildFeatureList(plan);
          const savings = annualSavingsCents(group);
          const isAnnual = plan.interval === "YEAR";

          return (
            <div
              key={group.family}
              className={`plan-card${plan.isFeatured ? " plan-card--featured" : ""}${
                isCurrent ? " plan-card--current" : ""
              }`}
            >
              {plan.isFeatured && !isCurrent && <span className="plan-badge">Mais popular</span>}
              {isCurrent && <span className="plan-badge plan-badge--current">Seu plano</span>}

              <p className="plan-card__name">{plan.name}</p>

              <div className="plan-card__price">
                <span className="plan-card__currency">R$</span>
                <span className="plan-card__amount">
                  {(plan.priceCents / 100).toLocaleString("pt-BR", {
                    minimumFractionDigits: plan.priceCents % 100 === 0 ? 0 : 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
                <span className="plan-card__period">
                  {isAnnual ? "/ano" : plan.interval === "MONTH" ? "/mês" : ""}
                </span>
              </div>

              <p className="plan-card__desc">{plan.description}</p>

              {isAnnual && plan.priceCents > 0 && (
                <p className="plan-card__savings">
                  Equivale a {formatBRL(Math.round(plan.priceCents / 12))}/mês
                  {plan.maxInstallments > 1 && ` · em até ${plan.maxInstallments}x`}
                  {savings > 0 && ` · economia de ${formatBRL(savings)}/ano`}
                </p>
              )}

              {!isAnnual && group.annual && savings > 0 && (
                <p className="plan-card__savings plan-card__savings--muted">
                  No anual: {formatBRL(group.annual.priceCents)}/ano · economize{" "}
                  {formatBRL(savings)}
                </p>
              )}

              <div className="plan-card__divider" />

              <ul className="plan-card__features">
                {highlights.map((feature) => (
                  <li key={feature} className="plan-card__feature">
                    <span className="plan-card__check">
                      <CheckIcon />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              {group.features.audience && (
                <p className="plan-card__audience">Para quem é: {group.features.audience}</p>
              )}

              <button
                type="button"
                className={`plan-card__cta ${
                  plan.isFeatured ? "plan-card__cta--dark" : "plan-card__cta--light"
                }`}
                disabled={isCurrent || plan.isFree}
                onClick={() => setCheckoutPlanItem(plan)}
              >
                {isCurrent
                  ? "Plano atual"
                  : plan.isFree
                    ? "Incluso na conta"
                    : `Assinar ${plan.name.replace("Plano ", "")}`}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Tabela comparativa oficial ─────────────────────────────────── */}
      {displayedGroups.length > 1 && (
        <section className="plan-compare">
          <h2 className="plan-compare__title">Compare os planos</h2>

          <div className="plan-compare__scroll">
            <table className="plan-compare__table">
              <thead>
                <tr>
                  <th scope="col">Funcionalidade / Benefício</th>
                  {displayedGroups.map((group) => (
                    <th
                      key={group.family}
                      scope="col"
                      data-current={group.plan.code === currentPlanCode}
                    >
                      {group.plan.name.replace(" Anual", "")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    {displayedGroups.map((group) => (
                      <td key={group.family}>{row.value(group)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="assinatura__note">
        Sem fidelidade · Cancele a qualquer momento · Pague com PIX, cartão ou boleto
      </p>

      <div className="assinatura__center">
        <button
          type="button"
          className="button button--ghost"
          onClick={() => navigate("/servicos")}
        >
          Ver destaques, banners e serviços avulsos →
        </button>
      </div>

      <CheckoutModal
        isOpen={Boolean(checkoutPlanItem)}
        onClose={() => setCheckoutPlanItem(null)}
        item={checkoutPlanItem}
        kind="PLAN"
        publicKey={catalog?.gateway?.publicKey}
        onSuccess={handleCheckoutSuccess}
      />
    </div>
  );
};

export default AssinaturaPage;
